import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import type { NostroAccount, NostroOperationResult } from '@/types/accounts';
import type { M3ApiResponse } from '@/types/api';
import { useNostroAccountsQuery, usePaymentIntermediariesQuery } from './api';
import { AccountDetailDialog } from './AccountDetailDialog';

function formatXAF(value: number): string {
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(value)} XAF`;
}

function creditColor(value: number): string {
  if (value > 0) return 'success.main';
  if (value < 0) return 'error.main';
  return 'warning.main';
}

interface AccountStats {
  dioolMain: number;
  bankPivot: number;
  externalAccounts: number;
}

function calcStats(records: NostroAccount[]): AccountStats {
  let dioolMain = 0;
  let bankPivot = 0;
  let externalAccounts = 0;

  for (const item of records) {
    if (item.accounts && item.accounts[0]) {
      const account = item.accounts[0];
      const total = item.daCredit + item.crCredit;
      if (account.provider.id === 0) {
        dioolMain += total;
      } else if (account.provider.id === 7) {
        bankPivot += total;
      } else {
        externalAccounts += total;
      }
    }
  }
  return { dioolMain, bankPivot, externalAccounts };
}

interface StatCardProps {
  amount: number;
  titleKey: string;
  titleDefault: string;
  color: string;
}

function StatCard({ amount, titleKey, titleDefault, color }: StatCardProps) {
  const { t } = useTranslation();
  return (
    <Card sx={{ flex: 1, minWidth: 200 }}>
      <CardContent sx={{ pb: 0 }}>
        <Typography variant="h5" fontWeight={700} color={color}>
          {formatXAF(amount)}
        </Typography>
      </CardContent>
      <Box sx={{ bgcolor: color, px: 2, py: 1, mt: 1, borderRadius: '0 0 4px 4px' }}>
        <Typography variant="body2" color="white">
          {t(titleKey, { defaultValue: titleDefault })}
        </Typography>
      </Box>
    </Card>
  );
}

export function AccountsPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();
  const accountsQuery = useNostroAccountsQuery();
  const intermediariesQuery = usePaymentIntermediariesQuery();
  const [filter, setFilter] = useState('');

  const records = accountsQuery.data ?? [];

  const stats = useMemo(() => calcStats(records), [records]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return records;
    const term = filter.toLowerCase();
    return records.filter(
      (r) =>
        r.accountName.toLowerCase().includes(term) ||
        String(r.crCredit).includes(term) ||
        String(r.daCredit).includes(term),
    );
  }, [records, filter]);

  const handleRowClick = async (record: NostroAccount) => {
    const result = await openDialog<M3ApiResponse<NostroOperationResult> | null>((close) => (
      <AccountDetailDialog
        nostroAccount={record}
        paymentIntermediaries={intermediariesQuery.data ?? []}
        onClose={close}
      />
    ));
    if (result !== null && result.code) {
      void accountsQuery.refetch();
    }
  };

  const handleError = async (message: string) => {
    await Swal.fire(t('messages.error', { defaultValue: 'Error' }), message, 'error');
  };

  if (accountsQuery.isError) {
    void handleError(t('messages.unknown-error', { defaultValue: 'An unknown error occurred' }));
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.menuAccounts', { defaultValue: 'Accounts' })}
      </Typography>

      {/* Stat cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <StatCard
          amount={stats.dioolMain}
          titleKey="pages.accounts.widget1.title"
          titleDefault="Diool Main"
          color="primary.main"
        />
        <StatCard
          amount={stats.bankPivot}
          titleKey="pages.accounts.widget2.title"
          titleDefault="Bank Pivot"
          color="success.light"
        />
        <StatCard
          amount={stats.externalAccounts}
          titleKey="pages.accounts.widget3.title"
          titleDefault="External Accounts"
          color="success.dark"
        />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('pages.accounts.filter', { defaultValue: 'Filter' })}
            sx={{ minWidth: 260 }}
          />
        </Box>

        {accountsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('pages.accounts.table.header.accountName', {
                      defaultValue: 'Account name',
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t('pages.accounts.table.header.depositAccount', {
                      defaultValue: 'Deposit account',
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t('pages.accounts.table.header.revenueAccount', {
                      defaultValue: 'Revenue account',
                    })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((record) => (
                  <TableRow
                    key={record.accountName}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(record)}
                  >
                    <TableCell>{record.accountName}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        component="span"
                        color={creditColor(record.daCredit)}
                      >
                        {formatXAF(record.daCredit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        component="span"
                        color={creditColor(record.crCredit)}
                      >
                        {formatXAF(record.crCredit)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && !accountsQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        {t('messages.no-data', { defaultValue: 'No data' })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
